import { FormEvent, useCallback, useEffect, useState } from 'react';
import { DataGrid } from '../components/DataGrid';
import { Link } from '../components/Links';
import { generatePassword, MIN_PASSWORD, UserRow, users } from '../api';
import { useAuth } from '../auth';
import { ago, dateTime } from '../format';

export const ROLE_LABEL = { admin: 'Admin', staff: 'Staff', customer: 'App login' } as const;

// Shows a password once, with a button to copy it, so the admin can pass it on.
export function PasswordField({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <label>Starting password <span className="muted small">(at least {MIN_PASSWORD} characters)</span>
      <span className="pwrow">
        <input id={id} type="text" autoComplete="off" spellCheck={false} required minLength={MIN_PASSWORD} className="mono"
          value={value} onChange={(e) => { onChange(e.target.value); setCopied(false); }} />
        <button type="button" onClick={() => { onChange(generatePassword()); setCopied(false); }}>Generate</button>
        <button type="button" disabled={!value} onClick={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true))}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </span>
    </label>
  );
}

export function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'staff' as 'admin' | 'staff', password: '' });
  const [formError, setFormError] = useState('');
  const [added, setAdded] = useState<{ name: string; email: string; password: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => users.list().then((r) => setRows(r.users)).catch((e) => setError(e.message)), []);
  useEffect(() => { void load(); }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormError('');
    try {
      await users.add(form);
      setAdded({ name: form.name, email: form.email.trim().toLowerCase(), password: form.password });
      setForm({ name: '', email: '', role: 'staff', password: '' });
      setAdding(false);
      void load();
    } catch (err) {
      setFormError((err as Error).message);
    }
    setBusy(false);
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>Users</h1>
        {!adding && <button className="primary" onClick={() => { setAdding(true); setAdded(null); setForm((f) => ({ ...f, password: generatePassword() })); }}>Add user</button>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}

      {added && (
        <div className="callout" role="status">
          <strong>{added.name} can now log in.</strong>
          <p>Give them the address <b>app.fleetfunctions.ie</b>, their email <b>{added.email}</b> and this starting password: <span className="mono"><b>{added.password}</b></span></p>
          <p className="small">They'll be asked to choose their own password the first time. This password isn't shown again.</p>
        </div>
      )}

      {adding && (
        <form className="card form" onSubmit={submit}>
          <h2>Add a user</h2>
          <div className="grid2">
            <label>Name<input id="u-name" type="text" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>Email<input id="u-email" type="email" required maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label>Role
              <select id="u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'staff' })}>
                <option value="staff">Staff: live map and journeys</option>
                <option value="admin">Admin: also vehicles and users</option>
              </select>
            </label>
            <PasswordField id="u-password" value={form.password} onChange={(password) => setForm({ ...form, password })} />
          </div>
          {formError && <p className="error" role="alert">{formError}</p>}
          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>{busy ? 'Adding…' : 'Add user'}</button>
            <button type="button" className="link" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}

      {rows && (
        <DataGrid id="users" rows={rows} rowKey={(u) => u.id} noun={['login', 'logins']} href={(u) => `/admin/users/${u.id}`}
          rowClass={(u) => (u.disabled ? 'dim' : undefined)} defaultSort={{ key: 'name', dir: 1 }} columns={[
            { key: 'name', label: 'Name', always: true, value: (u) => u.name,
              cell: (u) => <><Link to={`/admin/users/${u.id}`}><strong>{u.name}</strong></Link>{u.id === me?.id && <span className="muted small"> (you)</span>}</> },
            { key: 'email', label: 'Email', value: (u) => u.email },
            { key: 'role', label: 'Role', value: (u) => ROLE_LABEL[u.role] },
            { key: 'status', label: 'Status', value: (u) => (u.disabled ? 'Disabled' : u.mustChangePassword ? 'Must set password' : 'Active') },
            { key: 'login', label: 'Last login', value: (u) => u.lastLogin, sortBy: (u) => (u.lastLogin ? Date.parse(u.lastLogin) : null),
              csv: (u) => (u.lastLogin ? dateTime(u.lastLogin) : null), cell: (u) => (u.lastLogin ? <span title={dateTime(u.lastLogin)}>{ago(u.lastLogin)}</span> : <span className="muted">never</span>), nowrap: true },
          ]} />
      )}
    </div>
  );
}
